import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { StatusTag } from "../../../components/ui";
import {
  fetchQuestions,
  fetchAvailableTags,
  reorderQuestion,
  setAllFilters,
  selectQuestions,
  selectQuestionsStatus,
  selectQuestionsError,
  selectQuestionFilters,
} from "../../../redux/slices/question-bank-slice";
import { selectScopeJobs } from "../../../redux/slices/job-scope-slice";
import QuestionEditPanel from "./QuestionEditPanel";
import JobRoundPicker from "./JobRoundPicker";
import TemplatesTab from "./TemplatesTab";
import GenerateQuestionsModal from "./GenerateQuestionsModal";
import FilterRow from "./FilterRow";

const TYPE_ICONS = {
  VIDEO: { icon: "🎥", label: "Video", color: "#F59E0B" },
  MCQ: { icon: "📝", label: "MCQ", color: "#7C3AED" },
  BEHAVIORAL: { icon: "🗣", label: "Behavioral", color: "#2563EB" },
  SKILL: { icon: "⭐", label: "Skill", color: "#059669" },
  LIP_SYNC: { icon: "👄", label: "Lip-sync", color: "#84CC16" },
  PRACTICE: { icon: "🎯", label: "Practice", color: "#64748B" },
  CODING: { icon: "💻", label: "Coding", color: "#475569" },
};

const TYPE_TAG = (t) => {
  const meta = t ? TYPE_ICONS[t] : null;
  if (!meta) return <StatusTag type="slate" text={t || "—"} />;
  return (
    <span
      title={meta.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 7px",
        borderRadius: 100,
        fontSize: 10,
        fontWeight: 600,
        background: meta.color + "22",
        color: meta.color,
        whiteSpace: "nowrap",
      }}
    >
      <span>{meta.icon}</span>
      <span>{meta.label}</span>
    </span>
  );
};

const DIFF_TAG = (d) => {
  if (d === "EASY") return <StatusTag type="green" text="Easy" />;
  if (d === "MEDIUM") return <StatusTag type="lime" text="Medium" />;
  if (d === "HARD") return <StatusTag type="amber" text="Hard" />;
  if (d === "EXPERT") return <StatusTag type="violet" text="Expert" />;
  return null;
};

const mapBackendToForm = (q) => ({
  questionId: q.questionId ?? q.id ?? null,
  questionText: q.questionText || "",
  questionType: q.questionType || "VIDEO",
  responseType: q.responseType || "video",
  competency: q.competancy || "",
  defaultLanguage: q.defaultLanguage || "en",
  difficulty: q.difficulty || "MEDIUM",
  responseTimeInMinutes: q.responseTimeInMinutes ?? 1,
  responseTimeInSeconds: q.responseTimeInSeconds ?? 30,
  tags: Array.isArray(q.tags) ? q.tags : [],
  questionOptions: (() => {
    if (Array.isArray(q.questionOptions)) return q.questionOptions;
    if (typeof q.questionOptions === "string" && q.questionOptions) {
      try {
        return JSON.parse(q.questionOptions);
      } catch {
        return [];
      }
    }
    return [];
  })(),
  correctOption: q.correctOption || "",
  lipSyncVideoLink: q.lipSyncVideoLink || "",
  terminateInterview: !!q.terminateInterview,
  isMoveToQuestionBank: !!q.isMoveToQuestionBank,
  isPractice: !!q.isPractice,
  typeMetadata: (() => {
    if (q.typeMetadata == null) return null;
    if (typeof q.typeMetadata === "object") return q.typeMetadata;
    if (typeof q.typeMetadata === "string" && q.typeMetadata.trim()) {
      try { return JSON.parse(q.typeMetadata); } catch { return null; }
    }
    return null;
  })(),
});

const parseFiltersFromUrl = () => {
  const out = {};
  try {
    const sp = new URLSearchParams(window.location.search);
    const csv = (k) => (sp.get(k) ? sp.get(k).split(",").filter(Boolean) : []);
    out.types = csv("types");
    out.difficulties = csv("difficulties");
    out.tags = csv("tags");
    out.languages = csv("languages");
    out.search = sp.get("q") || "";
  } catch {
    // window unavailable (SSR) — empty
  }
  return out;
};

const filtersToQueryString = (filters) => {
  const sp = new URLSearchParams();
  if (filters.types?.length) sp.set("types", filters.types.join(","));
  if (filters.difficulties?.length)
    sp.set("difficulties", filters.difficulties.join(","));
  if (filters.tags?.length) sp.set("tags", filters.tags.join(","));
  if (filters.languages?.length)
    sp.set("languages", filters.languages.join(","));
  if (filters.search) sp.set("q", filters.search);
  return sp.toString();
};

const Questions = () => {
  const dispatch = useDispatch();
  const items = useSelector(selectQuestions);
  const status = useSelector(selectQuestionsStatus);
  const error = useSelector(selectQuestionsError);
  const filters = useSelector(selectQuestionFilters);

  const jobs = useSelector(selectScopeJobs);
  const [tab, setTab] = useState("thisJob");
  // Job + round always start empty on mount — no localStorage persistence —
  // so every visit to /admin/question-bank shows fresh dropdowns.
  const [jobId, setJobId] = useState(null);
  const [interviewRound, setInterviewRound] = useState("");

  const [localOrder, setLocalOrder] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  const jobTitle = useMemo(
    () => jobs.find((j) => j.id === jobId)?.name || "",
    [jobs, jobId],
  );
  const interviewId = useMemo(() => {
    if (!items.length) return null;
    return items[0].interviewId ?? items[0].interview_id ?? null;
  }, [items]);

  useEffect(() => {
    document.title = "Questions - Ample Hire";
    // Seed filters from URL on mount.
    dispatch(setAllFilters(parseFiltersFromUrl()));
    // Replay filter state when the user hits Back/Forward.
    const onPop = () => dispatch(setAllFilters(parseFiltersFromUrl()));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [dispatch]);

  // Debounce the free-text search input so we don't fetch per keystroke. Chip
  // changes (types/difficulties/languages/tags) still re-fetch immediately —
  // only the search term needs the pause.
  const [committedSearch, setCommittedSearch] = useState(filters.search || "");
  useEffect(() => {
    const timer = setTimeout(() => setCommittedSearch(filters.search || ""), 350);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Server-side filter dimensions trigger a fetch. Search rides on
  // committedSearch (debounced above) so chip clicks stay snappy while
  // typing isn't fetch-spammy.
  useEffect(() => {
    if (jobId && interviewRound) {
      dispatch(
        fetchQuestions({
          jobId,
          interviewRound,
          filters: {
            types: filters.types,
            difficulties: filters.difficulties,
            languages: filters.languages,
            tags: filters.tags,
            search: committedSearch,
          },
        }),
      );
    }
  }, [
    dispatch,
    jobId,
    interviewRound,
    filters.types,
    filters.difficulties,
    filters.languages,
    filters.tags,
    committedSearch,
  ]);

  // Mirror filters into the URL so the view is bookmarkable. Chip changes
  // (types/difficulties/languages/tags) push a history entry so Back/Forward
  // replays them. Free-text search is replace-only — pushing per keystroke
  // would flood the back-stack.
  const prevDiscreteRef = useRef(null);
  useEffect(() => {
    const qs = filtersToQueryString(filters);
    const next = window.location.pathname + (qs ? `?${qs}` : "");
    if (next === window.location.pathname + window.location.search) return;

    const discrete = JSON.stringify({
      types: filters.types || [],
      difficulties: filters.difficulties || [],
      languages: filters.languages || [],
      tags: filters.tags || [],
    });
    const isFirstRun = prevDiscreteRef.current === null;
    const discreteChanged = !isFirstRun && discrete !== prevDiscreteRef.current;
    prevDiscreteRef.current = discrete;

    if (discreteChanged) {
      window.history.pushState(null, "", next);
    } else {
      window.history.replaceState(null, "", next);
    }
  }, [filters]);

  useEffect(() => {
    // Sync localOrder to whatever the server returned.
    setLocalOrder(items.map((q) => q.questionId ?? q.id));
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const orderedItems = useMemo(() => {
    if (!localOrder.length) return items;
    const byId = new Map(items.map((q) => [q.questionId ?? q.id, q]));
    const out = [];
    for (const id of localOrder) {
      const q = byId.get(id);
      if (q) out.push(q);
    }
    // Any new items not yet in the order — append.
    for (const q of items) {
      const id = q.questionId ?? q.id;
      if (!localOrder.includes(id)) out.push(q);
    }
    return out;
  }, [items, localOrder]);

  // Search is now server-side via fetchQuestions (debounced). orderedItems
  // is already filtered — no extra client-side pass needed.
  const visibleItems = orderedItems;

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = localOrder.findIndex((id) => id === active.id);
    const newIdx = localOrder.findIndex((id) => id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;

    // Optimistic — reflect in UI immediately.
    setLocalOrder((current) => arrayMove(current, oldIdx, newIdx));

    if (interviewId == null) return; // safety; should never hit when list is loaded
    const result = await dispatch(
      reorderQuestion({
        interviewId,
        questionId: active.id,
        newOrder: newIdx + 1,
      }),
    );
    if (reorderQuestion.rejected.match(result)) {
      // Server didn't accept — refetch to resync with truth.
      dispatch(fetchQuestions({ jobId, interviewRound }));
    }
  };

  const openNewPanel = () => {
    setEditTarget(null);
    setEditOpen(true);
  };
  const openEditPanel = (q) => {
    setEditTarget(mapBackendToForm(q));
    setEditOpen(true);
  };
  const handleSaved = () => {
    if (jobId && interviewRound) {
      dispatch(fetchQuestions({ jobId, interviewRound }));
    }
    // Tags catalog may have grown after a save/accept — refresh so the filter
    // dropdown reflects newly-added tags next time it opens.
    dispatch(fetchAvailableTags());
  };

  const showThisJob = tab === "thisJob";
  const contextReady = jobId && interviewRound;

  return (
    <div
      className="app-content page-enter"
      style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 24px 32px" }}
    >
      <div style={hdrRow}>
        <div>
          <h1 style={hdrTitle}>Interview questions</h1>
          <p style={hdrSub}>
            {showThisJob
              ? "Per-job questions for the selected interview round"
              : "Reusable template library — clone any into a job"}
          </p>
        </div>
        {showThisJob && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setGenerateOpen(true)}
              disabled={!contextReady}
              style={{
                ...btnGhost,
                opacity: contextReady ? 1 : 0.5,
                cursor: contextReady ? "pointer" : "not-allowed",
              }}
            >
              ✨ Generate with AI
            </button>
            <button
              onClick={openNewPanel}
              disabled={!contextReady}
              style={{
                ...btnPrimary,
                opacity: contextReady ? 1 : 0.5,
                cursor: contextReady ? "pointer" : "not-allowed",
              }}
            >
              + Add
            </button>
          </div>
        )}
      </div>

      <div style={tabsRow}>
        <button
          onClick={() => setTab("thisJob")}
          style={{ ...tabBtn, ...(showThisJob ? tabActive : null) }}
        >
          This job's questions
        </button>
        <button
          onClick={() => setTab("templates")}
          style={{ ...tabBtn, ...(!showThisJob ? tabActive : null) }}
        >
          Templates
        </button>
      </div>

      {showThisJob ? (
        <>
          <JobRoundPicker
            jobId={jobId}
            interviewRound={interviewRound}
            onJobChange={setJobId}
            onRoundChange={setInterviewRound}
          />

          {!contextReady ? (
            <div style={emptyState}>Pick a job and interview round to see questions.</div>
          ) : (
            <>
              <FilterRow />
              {status === "loading" ? (
                <div style={emptyState}>Loading questions…</div>
              ) : status === "failed" ? (
                <div style={{ ...emptyState, color: "#991B1B" }}>{error || "Failed to load"}</div>
              ) : !visibleItems.length ? (
                <div style={emptyState}>
                  {orderedItems.length === 0
                    ? `No questions for this round yet. Click "+ Add" to create one, or switch to Templates to clone from your library.`
                    : "No questions match the current filters."}
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={visibleItems.map((q) => q.questionId ?? q.id)} strategy={verticalListSortingStrategy}>
                    {visibleItems.map((q, idx) => (
                      <SortableQuestion
                        key={q.questionId ?? q.id}
                        q={q}
                        index={idx + 1}
                        onEdit={() => openEditPanel(q)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </>
          )}

          <QuestionEditPanel
            open={editOpen}
            jobId={jobId}
            interviewRound={interviewRound}
            question={editTarget}
            onClose={() => setEditOpen(false)}
            onSaved={handleSaved}
          />

          <GenerateQuestionsModal
            open={generateOpen}
            jobId={jobId}
            jobTitle={jobTitle}
            interviewRound={interviewRound}
            onClose={() => setGenerateOpen(false)}
            onAcceptedAll={handleSaved}
          />
        </>
      ) : (
        <TemplatesTab />
      )}
    </div>
  );
};

const SortableQuestion = ({ q, index, onEdit }) => {
  const id = q.questionId ?? q.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    boxShadow: isDragging ? "0 8px 24px rgba(15,23,42,0.12)" : "none",
    zIndex: isDragging ? 1 : 0,
  };

  const timeSec = (Number(q.responseTimeInMinutes) || 0) * 60 + (Number(q.responseTimeInSeconds) || 0);

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: isDragging ? 0.5 : 1, y: 0 }}
      transition={{ duration: 0.15, delay: index * 0.02 }}
    >
      <div
        onClick={(e) => {
          if (e.target.closest("[data-drag-handle]")) return;
          onEdit?.();
        }}
        style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: 7,
          padding: "10px 14px",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
        }}
      >
        <button
          {...attributes}
          {...listeners}
          data-drag-handle
          aria-label="Drag handle"
          style={{
            background: "none",
            border: "none",
            padding: 4,
            cursor: "grab",
            color: "#94A3B8",
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ⠿
        </button>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "#7B5AC4", minWidth: 24, textAlign: "right",
          fontVariantNumeric: "tabular-nums", letterSpacing: "0.5px",
        }}>
          {String(index).padStart(2, "0")}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.45 }}>
            {q.questionText || "(no text)"}
          </div>
          {Array.isArray(q.tags) && q.tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {q.tags.map((t) => (
                <span key={t} style={tagPill}>{t}</span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {TYPE_TAG(q.questionType)}
          {DIFF_TAG(q.difficulty)}
          {timeSec > 0 && (
            <span style={{
              fontSize: 10, color: "#475569", background: "#F1F5F9", padding: "2px 7px", borderRadius: 100,
              fontWeight: 600, fontVariantNumeric: "tabular-nums",
            }}>
              {timeSec >= 60 ? `${Math.round(timeSec / 60)}m ${timeSec % 60 ? (timeSec % 60) + "s" : ""}`.trim() : `${timeSec}s`}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const hdrRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, gap: 16 };
const hdrTitle = { fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 };
const hdrSub = { fontSize: 13, color: "#64748B", margin: "4px 0 0" };
const btnPrimary = { padding: "8px 16px", borderRadius: 6, background: "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 12 };
const btnGhost = { padding: "8px 14px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12 };
const tabsRow = {
  display: "flex",
  gap: 4,
  borderBottom: "1px solid #E2E8F0",
  marginBottom: 16,
};
const tabBtn = {
  padding: "8px 14px",
  background: "none",
  border: "none",
  borderBottom: "2px solid transparent",
  fontSize: 12,
  fontWeight: 600,
  color: "#64748B",
  cursor: "pointer",
  marginBottom: -1,
};
const tabActive = {
  color: "#4338CA",
  borderBottomColor: "#7B5AC4",
};
const emptyState = {
  padding: 40,
  textAlign: "center",
  color: "#94A3B8",
  background: "white",
  border: "1px dashed #E2E8F0",
  borderRadius: 12,
  fontSize: 13,
};
const tagPill = {
  background: "#F1F5F9",
  color: "#475569",
  padding: "1px 7px",
  borderRadius: 100,
  fontSize: 10,
  fontWeight: 600,
};

export default Questions;
